/**
 * Session Utility Functions
 * Helper functions for managing and restoring TARA workflow sessions
 */

/**
 * Agent to step mapping (ISO 21434 order)
 */
const AGENT_STEP_MAP = {
  'analysis_agent': 1,
  'asset_identification_agent': 2,
  'damage_scenario_agent': 3,
  'threat_analysis_agent': 4,
  'attack_path_feasibility_agent': 5,
  'risk_assessment_agent': 6,
  'cybersecurity_goals_agent': 7,      // ISO 21434 Clause 9.4
  'cybersecurity_concept_agent': 8,    // ISO 21434 Clause 9.5
  'final_report_agent': 9
};

/**
 * Determine the current step based on completed agents
 * @param {Array<string>} completedAgents - List of completed agent names
 * @returns {number} Current step number (1-9)
 */
export const determineCurrentStep = (completedAgents) => {
  if (!completedAgents || completedAgents.length === 0) {
    return 1; // Start at step 1 if no agents completed
  }

  // Find the highest completed step
  let highestStep = 0;
  completedAgents.forEach(agent => {
    const step = AGENT_STEP_MAP[agent] || 0;
    if (step > highestStep) {
      highestStep = step;
    }
  });

  // Return the next step (current working step)
  // If all 9 steps are complete, stay on step 9
  return Math.min(highestStep + 1, 9);
};

/**
 * Check if a session is in progress
 * @param {Object} session - Session object from backend
 * @returns {boolean} True if session is in progress
 */
export const isSessionInProgress = (session) => {
  return session &&
         session.session_status === 'IN_PROGRESS' &&
         session.thread_id;
};

/**
 * Check if polling should be resumed for a session
 * @param {Object} session - Session object from backend
 * @returns {boolean} True if polling should be resumed
 */
export const shouldResumePolling = (session) => {
  if (!isSessionInProgress(session)) {
    return false;
  }

  // Check if not all agents are completed
  const completedAgents = session.agents_completed || session.completed_agents || [];
  const totalAgents = Object.keys(AGENT_STEP_MAP).length;

  return completedAgents.length < totalAgents;
};

/**
 * Get the current agent name for a step
 * @param {number} step - Step number (1-9)
 * @returns {string|null} Agent name or null if invalid step
 */
export const getAgentForStep = (step) => {
  const agentEntries = Object.entries(AGENT_STEP_MAP);
  const found = agentEntries.find(([agent, stepNum]) => stepNum === step);
  return found ? found[0] : null;
};

/**
 * Check if an agent is completed
 * @param {string} agentName - Name of the agent
 * @param {Array<string>} completedAgents - List of completed agents
 * @returns {boolean} True if agent is completed
 */
export const isAgentCompleted = (agentName, completedAgents = []) => {
  return completedAgents.includes(agentName);
};

/**
 * Get progress percentage based on completed agents
 * @param {Array<string>} completedAgents - List of completed agents
 * @returns {number} Progress percentage (0-100)
 */
export const calculateProgress = (completedAgents = []) => {
  const totalAgents = Object.keys(AGENT_STEP_MAP).length;
  const completedCount = completedAgents.filter(agent =>
    AGENT_STEP_MAP[agent] !== undefined
  ).length;

  return Math.round((completedCount / totalAgents) * 100);
};

/**
 * Create session restoration data
 * @param {Object} session - Session object from backend
 * @returns {Object} Restoration data for UI
 */
export const createRestorationData = (session) => {
  if (!session) return null;

  // Backend uses "agents_completed", frontend uses "completedAgents"
  const completedAgents = session.agents_completed || session.completed_agents || [];
  const currentAgent = session.current_agent || getAgentForStep(determineCurrentStep(completedAgents));
  const currentStep = determineCurrentStep(completedAgents);

  return {
    sessionId: session.session_id,
    threadId: session.thread_id,
    fileId: session.file_id,
    tenantId: session.tenant_id,
    analysisName: session.analysis_name || 'Untitled Analysis',
    currentStep,
    currentAgent,
    completedAgents: completedAgents,
    sessionStatus: session.session_status || session.status,
    createdAt: session.created_at,
    updatedAt: session.updated_at,
    progress: calculateProgress(completedAgents),
    shouldResumePolling: shouldResumePolling(session),
    isInProgress: isSessionInProgress(session),
    // Hierarchical TARA fields
    analysis_mode: session.analysis_mode || 'individual',
    tara_level: session.tara_level || 'unassigned',
    parent_sessions: session.parent_sessions || []
  };
};

/**
 * Get the next agent to process
 * @param {Array<string>} completedAgents - List of completed agents
 * @returns {string|null} Next agent name or null if all complete
 */
export const getNextAgent = (completedAgents = []) => {
  const allAgents = Object.keys(AGENT_STEP_MAP).sort(
    (a, b) => AGENT_STEP_MAP[a] - AGENT_STEP_MAP[b]
  );

  for (const agent of allAgents) {
    if (!completedAgents.includes(agent)) {
      return agent;
    }
  }

  return null; // All agents completed
};

/**
 * Check if workflow is complete
 * @param {Array<string>} completedAgents - List of completed agents
 * @returns {boolean} True if all agents are completed
 */
export const isWorkflowComplete = (completedAgents = []) => {
  const totalAgents = Object.keys(AGENT_STEP_MAP).length;
  const validCompletedAgents = completedAgents.filter(agent =>
    AGENT_STEP_MAP[agent] !== undefined
  );

  return validCompletedAgents.length === totalAgents;
};

/**
 * Generate welcome message for restored session
 * @param {Object} restorationData - Data from createRestorationData
 * @returns {string} Welcome message text
 */
export const generateRestorationMessage = (restorationData) => {
  if (!restorationData) {
    return 'Welcome back! Unable to restore previous session.';
  }

  const { analysisName, currentStep, progress } = restorationData;

  return `Welcome back to your analysis: **${analysisName}**

📊 Progress: ${progress}% complete
📍 Current Step: Step ${currentStep}
🔄 Resuming workflow...

Your analysis will continue from where you left off.`;
};